import { BatchCard, BatchCardViewModel } from '@components/UploadStatus/BatchCard';
import { BatchState } from '@components/UploadStatus/batchState';
import { UploadStatus } from '@components/UploadStatus/UploadStatus';
import { FOLDER_ROOT_PATH } from '@lib/library/folderPath';
import { MEDIA_COMMENT_ERROR } from '@lib/media/mediaCommentErrors';
import { ApiError } from '@lib/network/api';
import { LIBRARY_FOLDER_ERROR } from '@lib/network/libraryFolders';
import { facebookModule } from '@lib/social/scheduling/platforms/facebook';
import { youtubeModule } from '@lib/social/scheduling/platforms/youtube';
import { showToast } from '@lib/utils/toastmaster';
import { debugStore } from '@stores/Debug';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { notificationStore, RichEventIconKind, RichEventRow } from '@stores/Notifications';
import { CONNECTION_FAILURE_REASON } from '@stores/UploadManager/uploadFailure';
import { Media } from '@typings/Media';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './ToastDebugPage.module.scss';

// Minimal mock objects for templates that take typed args.
const mockFile = { name: 'video.mp4' } as File;
const mockMedia: Media = {
  metadata: { name: 'interview.mp4', filename: 'interview.mp4' },
  status: 'errored',
} as unknown as Media;

// ── Surface badges ─────────────────────────────────────────────────────
//
// Indicates the *planned* routing per `feat-desktop-toast-surface.md`
// Phase 5. Today every template still fires as a toast — Phase 5 narrows
// some of them to bell-only / dual-fire / panel-only. This page is
// agnostic of the current vs planned state; it shows where each
// notification *should* land once routing ships.
type Surface = 'toast' | 'bell' | 'panel';
const SURFACE_LABEL: Record<Surface, string> = {
  toast: '💬',
  bell: '🔔',
  panel: '📤',
};
const SURFACE_TITLE: Record<Surface, string> = {
  toast: 'Toast (bottom-center popup)',
  bell: 'Bell (notification panel)',
  panel: 'Upload panel (rendered as batch-card row)',
};

interface ToastDef {
  id: string;
  /**
   * The payload, or a function returning it.
   *
   * A function for any sample that has to **construct** something rather than write it down —
   * an `ApiError`, say. `SECTIONS` is a module-scope literal, so a constructor called inline
   * there runs during module evaluation, and this page is reached from inside the
   * `network/api` ↔ `stores/User` import cycle that `api.ts` warns about at its first line.
   * `ApiError` is still in its temporal dead zone at that point, and reading it throws
   * "Cannot access 'ApiError' before initialization" — which takes the whole router down with
   * it, not just this page. A factory is called at render instead, long after both modules
   * have finished evaluating.
   */
  data?: Record<string, unknown> | (() => Record<string, unknown>);
  /** Surfaces this template lands on. Defaults to ['toast']. */
  surfaces?: Surface[];
  /** Optional inline note (e.g. "warn variant"). */
  note?: string;
}

// HTML reference copy for each template, lifted verbatim from
// `starzero-notificationsFinal.html` lines 1778–1892. Used as a hover
// tooltip on each button so it's easy to see what the designer's
// prototype renders for the same template id. Keys are the
// toastmaster.tsx ids; the two billing templates were renamed
// (`not-enough-credits` → `upload-insufficient-credits`, same for
// storage) so we map both under the toastmaster name.
const HTML_REF: Record<string, { title: string; desc?: string; variant: string }> = {
  'upload-queued': {
    title: '"video.mp4" added to upload queue',
    desc: "Please don't leave this page.",
    variant: 'info',
  },
  'upload-cleared': { title: 'Upload of video.mp4 cleared', variant: 'info' },
  'uploads-all-cleared': { title: 'All uploads cleared', variant: 'info' },
  'upload-batch-complete': {
    title: 'Upload batch completed',
    desc: 'video.mp4 and 2 others uploaded successfully. You may now close this tab.',
    variant: 'success',
  },
  'duplicates-skipped': {
    title: '3 duplicates skipped',
    desc: 'Identical files already exist in this folder.',
    variant: 'info',
  },
  'playlist-uploading': {
    title: 'Adding playlist...',
    desc: 'Adding all 12 media in this playlist to My Library.',
    variant: 'info',
  },
  'playlist-done': { title: 'Playlist added', variant: 'success' },
  'folder-uploading': {
    title: 'Adding Google Drive folder...',
    desc: 'Adding all 12 items from “Raw Footage” to My Library.',
    variant: 'info',
  },
  'folder-done': { title: 'Google Drive folder added', variant: 'success' },
  'media-too-long': {
    title: "Media's too long",
    desc: 'This media exceeds the 10-hour limit. Contact us if you need a higher limit.',
    variant: 'error',
  },
  'channel-uploading': {
    title: 'Adding channel',
    desc: 'Adding all 50 media in this channel to My Library. This may take a while...',
    variant: 'info',
  },
  'channel-done': {
    title: 'Channel added',
    desc: "We've added all 50 media in this channel and we are now indexing them for faster search.",
    variant: 'success',
  },
  'single-video-uploading': { title: 'Adding media...', variant: 'info' },
  'single-video-done': {
    title: 'Media added',
    desc: "We've added the media and we are now indexing it for faster search.",
    variant: 'success',
  },
  'library-add-failed': {
    title: 'Failed to add media to My Library',
    desc: 'An error occurred while adding media to your library. Please try again.',
    variant: 'error',
  },
  'media-upload-cancelled': {
    title: 'Import cancelled',
    desc: 'Some media might have been added before the cancellation operation happened.',
    variant: 'info',
  },
  'media-delete-success-single': {
    title: 'video.mp4 deleted.',
    desc: 'video.mp4 deleted from the library My Library.',
    variant: 'success',
  },
  'media-delete-error-single': {
    title: 'Failed to delete video.mp4.',
    desc: 'Could not delete video.mp4 from the library My Library. Please try again. If the problem persists, please contact support.',
    variant: 'error',
  },
  'media-delete-success': {
    title: 'Media deletion complete',
    desc: '3 media were deleted. This operation freed 1.2 GB of storage.',
    variant: 'success',
  },
  'media-delete-error': {
    title: 'Media deletion incomplete',
    desc: '2 media failed to delete. Please try again. If the problem persists, please contact support.',
    variant: 'error',
  },
  'failed-media-clear-success': {
    title: 'Cleared failed media',
    desc: 'All failed media have been removed.',
    variant: 'success',
  },
  'failed-media-clear-error': {
    title: 'Failed to clear failed media',
    desc: 'An error occurred while clearing failed media. Please try again.',
    variant: 'error',
  },
  'failed-media-clear-error-unexpected': {
    title: 'Failed to clear failed media',
    desc: 'An unexpected error occurred. Please try again.',
    variant: 'error',
  },
  'media-link-copied': {
    title: 'Link copied',
    desc: 'Media link copied to clipboard',
    variant: 'success',
  },
  'media-link-copied-at-time': {
    title: 'Link copied',
    desc: 'Media link copied to clipboard — opens at 1:30',
    variant: 'success',
  },
  'media-link-copy-failed': {
    title: 'Failed to copy link',
    desc: 'Could not copy link to clipboard',
    variant: 'error',
  },
  'media-original-url-unavailable': {
    title: 'Original URL unavailable',
    desc: 'This media was not imported from a URL.',
    variant: 'error',
  },
  'media-original-playback-failed': {
    title: 'Playing the preview instead',
    desc: 'Copy depends on the reason — the browser refusing the format, or the file not loading.',
    variant: 'error',
  },
  'media-downloading': {
    title: 'Downloading video.mp4...',
    desc: 'It might take some time to download media. Hang tight!',
    variant: 'info',
  },
  'media-download-failed': {
    title: 'Failed to download media',
    desc: 'Please contact support.',
    variant: 'error',
  },
  'media-retry-failed': {
    title: 'Retry failed',
    desc: 'Could not retry processing. Please try again or contact support.',
    variant: 'error',
  },
  'media-modal-not-ready': {
    title: 'Media not yet ready',
    desc: "We are currently indexing the media. You'll be able to view visual summaries, view transcripts, and rapidly search for moments in it once indexing is complete.",
    variant: 'info',
  },
  'library-modify-success': {
    title: 'Library modified',
    desc: 'Library name changed from Old Name to New Name. Its thumbnail also changed, it is now represented by the thumbnail of Beach Clip.',
    variant: 'success',
  },
  'library-modify-error': {
    title: 'Library modification failed',
    desc: 'Library My Library has not been modified.',
    variant: 'error',
  },
  'library-read-only': {
    title: 'This library is read-only',
    desc: 'You cannot add media to read-only libraries.',
    variant: 'error',
  },
  'library-no-selection': {
    title: 'No library selected',
    desc: 'Switching to demo library',
    variant: 'info',
  },
  'library-selection-changed': { title: 'Library successfully changed', variant: 'success' },
  'library-unavailable': {
    title: 'That library is no longer available',
    desc: 'Switching to the demo library',
    variant: 'error',
  },
  'moment-not-found': { title: 'Moment not found', variant: 'error' },
  'moment-range-not-selected': { title: 'No moment range selected', variant: 'error' },
  'moment-update-failed': { title: 'Failed to update moment', variant: 'error' },
  'moment-downloading': {
    title: 'Downloading 3 moments...',
    desc: 'Please do not close this tab.',
    variant: 'info',
  },
  'moment-download-failed-individual': {
    title: 'Failed to download moment 2',
    desc: 'Error: Network timeout',
    variant: 'error',
  },
  'moment-download-complete': {
    title: 'Download complete',
    desc: 'Successfully downloaded 3 moments',
    variant: 'success',
  },
  'moment-download-failed': {
    title: 'Download failed',
    desc: 'Failed to download moments: Server error',
    variant: 'error',
  },
  'comment-link-copied': {
    title: 'Link copied',
    desc: 'Comment link copied to clipboard — opens on the comment at 1:08:25 – 1:25:23',
    variant: 'success',
  },
  'comment-link-not-found': {
    title: 'Comment not found',
    desc: 'It may have been deleted. The media is open where it pointed.',
    variant: 'error',
  },
  'media-comment-failed': {
    title: "Couldn't post your comment",
    desc: 'This media is holding as many comments as it can. Delete one to make room.',
    variant: 'error',
  },
  'image-drop-rejected': {
    title: 'Image drop rejected',
    desc: 'Image search is not supported while in conversational search mode. Please switch to visual or unified search mode to search with image queries.',
    variant: 'error',
  },
  'file-drop-rejected': {
    title: 'File drop rejected',
    desc: 'Only image files are accepted here, and image search requires visual or unified search mode. Please switch modes and drop a supported image file.',
    variant: 'error',
  },
  'image-paste-rejected': {
    title: 'Image paste rejected',
    desc: 'Image search is not supported while in conversational search mode. Please switch to visual or unified search mode to search with image queries.',
    variant: 'error',
  },
  'file-paste-rejected': {
    title: 'File paste rejected',
    desc: 'Only image files are accepted here, and image search requires visual or unified search mode. Please switch modes and paste a supported image file.',
    variant: 'error',
  },
  'unsupported-file-type': {
    title: 'Unsupported file type',
    desc: 'Supported formats for image search queries: jpg, jpeg, png, gif, webp.',
    variant: 'error',
  },
  'multiple-images-detected': {
    title: 'Multiple images detected',
    desc: 'Image search only supports one image at a time. Using the first image.',
    variant: 'error',
  },
  'search-mode-updated': {
    title: 'Search mode updated',
    desc: "You're now viewing visual results.",
    variant: 'success',
  },
  'credits-refresh-error': {
    title: 'Error refreshing credits',
    desc: 'An unexpected error occurred when refreshing your credits. Please wait a few moments then try again. If the error persists, please contact support.',
    variant: 'error',
  },
  'topup-rate-limited': {
    title: 'Too many requests',
    desc: 'Please wait a moment before trying again.',
    variant: 'error',
  },
};

function buttonTooltip(id: string, data?: Record<string, unknown>): string {
  const call = `showToast('${id}'${data ? `, ${JSON.stringify(data)}` : ''})`;
  const ref = HTML_REF[id];
  if (!ref) return call;
  const descLine = ref.desc ? `\nDesc: ${ref.desc}` : '';
  return `${call}\n\n— HTML reference (${ref.variant}) —\nTitle: ${ref.title}${descLine}`;
}

// Cross-check vs HTML reference (notif-html-surfaces-verified.md):
//
// All 58 toast templates from the prototype's TPL block are present in
// `toastmaster.tsx` — the two billing entries were renamed
// (`not-enough-*` → `upload-insufficient-*`) but otherwise the set is
// covered. The HTML's prototype also fires 9 bell-only batch summaries
// from its upload state machine — those are NOT separate templates here
// because they're already covered by existing implementation:
//
//   HTML bell entry                          | Where it lives today
//   ---------------------------------------- | --------------------
//   Upload complete                          | `upload-batch-complete` (success path)
//   Upload failed (all-fail)                 | `upload-batch-complete` (failed path)
//   Upload finished with errors (partial)    | `upload-batch-complete` (mixed path)
//   Upload failed · Not enough credits       | BatchCard `BillingActionRow` (3-CTA stack)
//   Upload failed · Not enough storage       | BatchCard `BillingActionRow` (3-CTA stack)
//   Upload complete · credit overage         | not implemented — credit usage line is new UX
//   Upload complete (with credit usage line) | not implemented — credit usage line is new UX
//
// The first 5 use existing inline affordances on the BatchCard. The
// last 2 (credit-usage display + overage warn) are new UX with no
// concrete phase assigned yet — most natural home is alongside the
// bell UI when that surface gets built.

interface ToastSection {
  title: string;
  /** Optional note rendered below the title (e.g. variant hint). */
  hint?: string;
  toasts: ToastDef[];
}

/**
 * A refusal from the comment routes, shaped as the panel would actually receive it.
 *
 * Only ever called from inside a `data` factory — see `ToastDef.data` for why calling it where
 * `SECTIONS` is written brings the router down.
 */
const commentError = (code: string) => new ApiError(code, 'sample', 422);
const folderError = (code: string) => new ApiError(code, 'sample', 409);

// Mirrors the grouping in `src/lib/utils/toastmaster.tsx` so this page is a
// 1:1 catalog of every defined template. Order also matches.
const SECTIONS: ToastSection[] = [
  {
    title: 'Upload (file)',
    toasts: [
      { id: 'upload-queued', data: { files: [mockFile] }, note: 'info' },
      { id: 'upload-cleared', data: { filename: 'video.mp4' }, note: 'info' },
      { id: 'uploads-all-cleared', note: 'info' },
      {
        id: 'upload-batch-complete',
        data: {
          nSuccess: 3,
          nFailed: 0,
          totalAttempted: 3,
          hasActiveUploads: false,
          successFilename: 'video.mp4',
        },
        surfaces: ['bell'],
        note: 'spec: batch-level completion → bell, not toast',
      },
      { id: 'duplicates-skipped', data: { count: 3 }, note: 'info' },
    ],
  },
  {
    title: 'Add (URL / playlist / channel)',
    toasts: [
      {
        id: 'playlist-uploading',
        data: { count: 12, firstTitle: 'My Video', libraryName: 'My Library' },
        note: 'info',
      },
      { id: 'playlist-done' },
      {
        id: 'folder-uploading',
        data: { count: 12, folderName: 'Raw Footage', libraryName: 'My Library' },
        note: 'Google Drive folder',
      },
      { id: 'folder-done' },
      { id: 'media-too-long', note: 'single URL over the 10h limit' },
      {
        id: 'channel-uploading',
        data: { count: 50, libraryName: 'My Library' },
        note: 'info',
      },
      { id: 'channel-done', data: { count: 50 } },
      { id: 'single-video-uploading', note: 'info' },
      { id: 'single-video-done' },
    ],
  },
  {
    title: 'Upload (add / cancel)',
    toasts: [
      {
        id: 'library-add-failed',
        data: { libraryName: 'My Library', message: 'Network error' },
      },
      { id: 'media-import-cancelled', note: 'info' },
      { id: 'media-upload-cancelled', note: 'info' },
    ],
  },
  {
    title: 'Media (delete)',
    toasts: [
      {
        id: 'media-delete-success-single',
        data: { filename: 'video.mp4', libraryName: 'My Library' },
      },
      {
        id: 'media-delete-error-single',
        data: { filename: 'video.mp4', libraryName: 'My Library' },
      },
      {
        id: 'media-delete-success',
        data: {
          libraryName: 'My Library',
          count: 3,
          storageFreed: '1.2 GB',
          filenames: ['intro.mp4', 'middle.mp4', 'outro.mp4'],
        },
      },
      {
        id: 'media-delete-error',
        data: {
          libraryName: 'My Library',
          count: 2,
          filenames: ['intro.mp4', 'middle.mp4'],
          // Demo retry handler so the bell's "More info" modal renders its
          // "Retry all" footer button. Real callers (MediaDeleteModal) pass
          // a real closure that re-invokes the delete API.
          onRetry: () => {
            console.log('[debug] media-delete-error retry stub invoked');
          },
        },
      },
      { id: 'failed-media-clear-success' },
      { id: 'failed-media-clear-error' },
      { id: 'failed-media-clear-error-unexpected' },
    ],
  },
  {
    title: 'Media (actions)',
    toasts: [
      { id: 'media-link-copied' },
      { id: 'media-link-copied-at-time', data: { seconds: 90 }, note: 'shared with a ?t=' },
      { id: 'media-link-copy-failed' },
      { id: 'media-original-url-unavailable' },
      {
        id: 'media-original-playback-failed',
        data: { reason: 'unsupported-format' },
        note: 'quality menu fell back — browser refused the format',
      },
      {
        id: 'media-original-playback-failed',
        data: { reason: 'load-failed' },
        note: 'quality menu fell back — file never loaded',
      },
      { id: 'media-downloading', data: { filename: 'video.mp4' }, note: 'info' },
      { id: 'media-download-failed' },
      { id: 'media-retry-failed', data: { error: 'Internal server error' } },
    ],
  },
  {
    title: 'Media (other)',
    toasts: [{ id: 'media-modal-not-ready', data: { media: mockMedia }, note: 'info' }],
  },
  {
    title: 'Library',
    toasts: [
      {
        id: 'library-modify-success',
        data: {
          name: 'New Name',
          oldName: 'Old Name',
          thumbnailMediaId: 'mock-id',
          nameChanged: true,
          thumbnailChanged: true,
          oldMediaTitle: 'Old Clip',
          newMediaTitle: 'Beach Clip',
        },
      },
      { id: 'library-modify-error', data: { name: 'My Library', thumbnailMediaId: 'mock-id' } },
      { id: 'library-delete-success' },
      { id: 'library-delete-failed', data: () => ({ error: new Error('mock delete failure') }) },
      { id: 'library-detach-success', note: "removed someone else's library" },
      { id: 'library-detach-failed', data: () => ({ error: new Error('mock detach failure') }) },
      { id: 'library-read-only' },
      { id: 'library-no-selection', note: 'info' },
      { id: 'library-selection-changed' },
      { id: 'library-unavailable', note: 'selected library was deleted or unshared' },
    ],
  },
  {
    title: 'Moments',
    toasts: [
      { id: 'moment-not-found' },
      { id: 'moment-range-not-selected' },
      { id: 'moment-update-failed' },
      { id: 'moment-downloading', data: { count: 3 }, note: 'info' },
      {
        id: 'moment-download-failed-individual',
        data: { index: 2, error: 'Network timeout' },
      },
      { id: 'moment-download-complete', data: { count: 3 } },
      { id: 'moment-download-failed', data: { error: 'Server error' } },
    ],
  },
  {
    title: 'Comments',
    toasts: [
      // One template, six headers. The sample errors are real `ApiError`s because the
      // description is chosen by `code` — a plain Error would only ever show the fallback.
      {
        id: 'media-comment-failed',
        data: () => ({
          action: 'post',
          error: commentError(MEDIA_COMMENT_ERROR.commentLimitReached),
        }),
        note: 'at the cap',
      },
      {
        id: 'media-comment-failed',
        data: () => ({
          action: 'reply',
          error: commentError(MEDIA_COMMENT_ERROR.replyLimitReached),
        }),
      },
      {
        id: 'media-comment-failed',
        data: () => ({ action: 'edit', error: commentError(MEDIA_COMMENT_ERROR.forbidden) }),
        note: "someone else's",
      },
      {
        id: 'media-comment-failed',
        data: () => ({ action: 'delete', error: commentError(MEDIA_COMMENT_ERROR.notFound) }),
      },
      {
        id: 'media-comment-failed',
        data: () => ({ action: 'post', error: commentError(MEDIA_COMMENT_ERROR.conflict) }),
        note: 'retryable',
      },
      {
        id: 'media-comment-failed',
        data: () => ({ action: 'post', error: new Error('offline') }),
        note: 'anything else',
      },
      { id: 'comment-link-copied', data: { label: '1:08:25 – 1:25:23' } },
      { id: 'comment-link-not-found', note: 'link to a deleted comment' },
    ],
  },
  {
    title: 'Folders',
    toasts: [
      {
        id: 'folder-action-failed',
        data: () => ({ action: 'create', error: folderError(LIBRARY_FOLDER_ERROR.alreadyExists) }),
        note: 'name taken',
      },
      {
        id: 'folder-action-failed',
        data: () => ({ action: 'move', error: folderError(LIBRARY_FOLDER_ERROR.pathTooLong) }),
      },
      {
        id: 'folder-action-failed',
        data: () => ({ action: 'move-media', error: folderError(LIBRARY_FOLDER_ERROR.notFound) }),
        note: 'folder gone',
      },
      { id: 'folder-deleted', data: { name: 'Lisbon', mediaCount: 12 } },
      { id: 'folder-deleted', data: { name: 'Drafts', mediaCount: 0 }, note: 'empty folder' },
      {
        id: 'folder-deleted',
        data: { name: 'Lisbon', mediaCount: 0, kept: { count: 12, destination: 'Trips' } },
        note: 'media kept',
      },
      {
        id: 'media-moved-to-folder',
        data: { count: 3, destination: 'Lisbon', undo: () => {} },
      },
      {
        id: 'folder-moved',
        data: { name: 'Day 1', destination: 'Lisbon', undo: () => {} },
      },
      {
        id: 'folder-action-failed',
        data: () => ({ action: 'undo', error: folderError(LIBRARY_FOLDER_ERROR.notFound) }),
        note: 'undo, folder gone',
      },
      {
        id: 'media-move-undo-incomplete',
        data: () => ({
          restored: 4,
          stranded: 2,
          destination: 'Lisbon',
          error: folderError(LIBRARY_FOLDER_ERROR.notFound),
        }),
        note: 'undo across folders, one gone',
      },
      {
        id: 'folder-create-for-upload-failed',
        data: () => ({ count: 2, error: folderError(LIBRARY_FOLDER_ERROR.tooMany) }),
        surfaces: ['toast', 'bell'],
        note: 'dropped directory',
      },
    ],
  },
  {
    title: 'Search / image',
    toasts: [
      { id: 'image-drop-rejected' },
      { id: 'file-drop-rejected' },
      { id: 'image-paste-rejected' },
      { id: 'file-paste-rejected' },
      { id: 'unsupported-file-type' },
      { id: 'multiple-images-detected' },
      { id: 'search-mode-updated', data: { descriptionType: 'visual' } },
    ],
  },
  {
    title: 'Billing / credits',
    toasts: [
      { id: 'credits-refresh-error' },
      { id: 'subscription-checkout-error', data: { error: 'subscription already exists' } },
      { id: 'topup-rate-limited' },
    ],
  },
  {
    title: 'Chats / agents',
    toasts: [{ id: 'chat-open-error', data: { error: 'token exchange failed' } }],
  },
  {
    title: 'Account',
    toasts: [
      { id: 'nickname-changed', data: { nickname: 'jared' } },
      { id: 'nickname-change-failed' },
      { id: 'password-change-success', note: 'regular change' },
      {
        id: 'password-change-success',
        data: { set: true },
        note: 'magic-link user, first password',
      },
      { id: 'password-change-failed', note: 'regular change' },
      {
        id: 'password-change-failed',
        data: { set: true },
        note: 'magic-link user, first password',
      },
    ],
  },
  {
    title: 'Publishing',
    toasts: [
      {
        id: 'social-connect-success',
        data: { platformLabel: 'TikTok', accountNoun: 'account', count: 1, names: ['@starzero'] },
      },
      {
        id: 'social-connect-success',
        data: { platformLabel: 'TikTok', accountNoun: 'account', count: 1, names: [] },
        note: 'name not loaded',
      },
      {
        id: 'social-connect-success',
        data: {
          platformLabel: 'Facebook',
          accountNoun: 'Page',
          count: 3,
          names: ['Testing Stuff', 'StarZero', 'Moments'],
        },
        note: 'three Pages in one login',
      },
      {
        id: 'social-connect-success',
        data: { platformLabel: 'Facebook', accountNoun: 'Page', count: 2, names: [] },
        note: 'two Pages, names not loaded',
      },
      {
        id: 'social-connect-failed',
        data: {
          platformLabel: 'Facebook',
          errorCode: 'no_accounts',
          noAccountsNote: facebookModule.noAccountsNote,
        },
        note: 'login shared no Page',
      },
      {
        id: 'social-connect-failed',
        data: {
          platformLabel: 'YouTube',
          errorCode: 'no_accounts',
          noAccountsNote: youtubeModule.noAccountsNote,
        },
        note: 'login shared no channel',
      },
      {
        id: 'social-connect-failed',
        data: { platformLabel: 'Threads', errorCode: 'no_accounts', noAccountsNote: null },
        note: 'a platform without a module: the generic line',
      },
      {
        id: 'social-connect-failed',
        data: { platformLabel: 'TikTok', errorCode: 'invalid_state', noAccountsNote: null },
      },
      {
        id: 'social-connect-failed',
        data: { platformLabel: 'TikTok', errorCode: 'something_new', noAccountsNote: null },
        note: 'unknown code',
      },
      {
        id: 'social-connect-success',
        data: { platformLabel: 'YouTube', accountNoun: 'channel', count: 1, names: ['StarZero'] },
      },
      {
        id: 'social-disconnect-success',
        data: { platformLabel: 'TikTok', accountNoun: 'account' },
      },
      { id: 'social-disconnect-success', data: { platformLabel: 'Facebook', accountNoun: 'Page' } },
      {
        id: 'social-disconnect-failed',
        data: { platformLabel: 'TikTok', accountNoun: 'account', error: new Error('boom') },
      },
      { id: 'social-post-scheduled', data: { count: 1, now: false } },
      { id: 'social-post-scheduled', data: { count: 3, now: true }, note: 'post now, 3 accounts' },
      { id: 'social-post-saved' },
      { id: 'social-post-cancelled' },
      { id: 'social-post-retried' },
      { id: 'social-post-status-changed', data: { statusLabel: 'Publishing' } },
      {
        id: 'social-post-action-failed',
        data: { action: 'schedule', description: 'The account needs to be reconnected first.' },
      },
      {
        id: 'social-post-action-failed',
        data: { action: 'retry', description: 'Something went wrong. Try again later.' },
        note: 'generic',
      },
    ],
  },
  {
    title: 'App',
    toasts: [
      {
        id: 'section-render-failed',
        note: 'fired by SubtreeErrorBoundary; has a stable id, so clicking twice replaces the toast instead of stacking',
      },
    ],
  },
];

// ── Fake upload-bar batches ────────────────────────────────────────────
//
// Lets you eyeball the upload status panel without performing a real
// upload. We add notifications in *terminal* states so the 5s polling
// loop (which reads `notificationStore.activeMediaNotifications`) skips
// them — fake mediaIds would otherwise 404 and silently delete the
// notification within a polling cycle.
//
// In-flight states (queued / preparing / uploading / wrapping-up /
// processing) are not simulated here because deriveBatchState reads
// uploadManager state which we can't easily spoof from the outside.
// Real uploads are still the way to exercise those states.
//
// libraryId must be a real one the user has access to — anything else
// trips either the ObjectId validator (400) or the auth check (403).
// We pull it from libraryManager at render time. mediaIds are fake
// 24-char hex; the user's real library doesn't contain them, so
// BatchCard's useMediaThumbnail / AuthenticatedImage gets a 404
// (silently swallowed by AuthenticatedImage + the 5s polling cycle).

let fakeIdSeq = 0;
const nextFakeMediaId = () => {
  fakeIdSeq++;
  return fakeIdSeq.toString(16).padStart(24, '0');
};
const nextFakeSeed = () => `seed-${fakeIdSeq}`;

function addFakeSuccessBatch(libraryId: string) {
  const allIds = [nextFakeMediaId(), nextFakeMediaId(), nextFakeMediaId()];
  notificationStore.add({
    type: 'upload-file',
    title: 'Test Library',
    thumbnailUrl: null,
    secondaryThumbnailUrl: null,
    gradientSeed: nextFakeSeed(),
    secondaryGradientSeed: nextFakeSeed(),
    isAudio: false,
    uploadProgress: 1,
    processingProgress: 1,
    uploadUuids: [],
    mediaIds: allIds,
    failedUploads: [],
    libraryId,
    folderPath: FOLDER_ROOT_PATH,
    libraryName: 'Test Library',
    mediaTitles: ['video1.mp4', 'video2.mp4', 'video3.mp4'],
    completedAt: Date.now(),
  });
}

function addFakePartialFailureBatch(libraryId: string) {
  const failedIds = [nextFakeMediaId(), nextFakeMediaId()];
  const allIds = [...failedIds, nextFakeMediaId(), nextFakeMediaId(), nextFakeMediaId()];
  const id = notificationStore.add({
    type: 'upload-file',
    title: 'Test Library',
    thumbnailUrl: null,
    secondaryThumbnailUrl: null,
    gradientSeed: nextFakeSeed(),
    secondaryGradientSeed: nextFakeSeed(),
    isAudio: false,
    uploadProgress: 1,
    processingProgress: 1,
    uploadUuids: [],
    mediaIds: allIds,
    failedUploads: [],
    libraryId,
    folderPath: FOLDER_ROOT_PATH,
    libraryName: 'Test Library',
    mediaTitles: allIds.map((_, i) => `video${i + 1}.mp4`),
    completedAt: Date.now(),
  });
  // Mutate erroredMediaIds directly (it's set to [] inside add()).
  const notif = notificationStore.notifications.find((n) => n.id === id);
  if (notif && notif.type === 'upload-file') {
    notif.erroredMediaIds = failedIds;
  }
}

function addFakeTotalFailureBatch(libraryId: string) {
  const allIds = [nextFakeMediaId(), nextFakeMediaId(), nextFakeMediaId()];
  const id = notificationStore.add({
    type: 'upload-file',
    title: 'Test Library',
    thumbnailUrl: null,
    secondaryThumbnailUrl: null,
    gradientSeed: nextFakeSeed(),
    secondaryGradientSeed: nextFakeSeed(),
    isAudio: false,
    uploadProgress: 1,
    processingProgress: 1,
    uploadUuids: [],
    mediaIds: allIds,
    failedUploads: [],
    libraryId,
    folderPath: FOLDER_ROOT_PATH,
    libraryName: 'Test Library',
    mediaTitles: allIds.map((_, i) => `video${i + 1}.mp4`),
    completedAt: Date.now(),
  });
  const notif = notificationStore.notifications.find((n) => n.id === id);
  if (notif && notif.type === 'upload-file') {
    notif.erroredMediaIds = allIds;
  }
}

// The customer case from the flaky-link incident: nothing reached the server, so
// the batch has no media and only its failure record describes it.
function addFakeUploadFailureBatch(libraryId: string) {
  notificationStore.add({
    type: 'upload-file',
    title: 'Test Library',
    thumbnailUrl: null,
    secondaryThumbnailUrl: null,
    gradientSeed: nextFakeSeed(),
    secondaryGradientSeed: null,
    isAudio: false,
    uploadProgress: 1,
    processingProgress: 1,
    uploadUuids: [],
    mediaIds: [],
    failedUploads: [
      { fileName: 'IMG_4253.mov', reason: CONNECTION_FAILURE_REASON, category: 'connection' },
    ],
    libraryId,
    folderPath: FOLDER_ROOT_PATH,
    libraryName: 'Test Library',
    mediaTitles: ['IMG_4253.mov'],
    completedAt: Date.now(),
  });
}

function addFakeUrlImportBatch(libraryId: string) {
  notificationStore.add({
    type: 'add-media-via-url',
    title: 'YouTube playlist',
    thumbnailUrl: null,
    secondaryThumbnailUrl: null,
    gradientSeed: nextFakeSeed(),
    secondaryGradientSeed: nextFakeSeed(),
    isAudio: false,
    progress: 1,
    mediaIds: [nextFakeMediaId(), nextFakeMediaId()],
    libraryId,
    folderPath: FOLDER_ROOT_PATH,
    libraryName: 'Test Library',
    mediaTitles: ['Imported video 1', 'Imported video 2'],
    completedAt: Date.now(),
  });
}

function clearAllBatches() {
  notificationStore.clear();
}

// ── In-flight BatchCard simulation ─────────────────────────────────────
//
// Mounts the (dumb) `<BatchCard>` directly with a hand-built VM — no
// notificationStore, no uploadManager — so the in-flight states the live
// wrapper would otherwise need real upload activity for can be rendered
// purely off a state machine running inside the debug page.
//
// Each lifecycle stage carries a fixed duration. A 100ms tick advances
// the active stage's progress until either (a) progress hits the stage's
// end, in which case we move to the next stage and reset progress, or
// (b) we land on the terminal state and the simulation stops ticking.
// Multiple simulations can run concurrently; they all share one tick.

interface SimStage {
  state: BatchState;
  durationMs: number;
  /** Where displayProgress lands at the end of this stage (start is the
   * previous stage's end, or 0 for the first stage). */
  progressEnd: number;
  /** Optional time-line copy for this stage. */
  timeLineText: string | null;
}

const FULL_LIFECYCLE: SimStage[] = [
  { state: 'queued', durationMs: 1500, progressEnd: 0, timeLineText: 'Please wait…' },
  {
    state: 'preparing',
    durationMs: 1500,
    progressEnd: 0,
    timeLineText: 'Calculating ETA…',
  },
  {
    state: 'uploading',
    durationMs: 3000,
    progressEnd: 0.6,
    timeLineText: 'About 5 sec left at 4 MB/s',
  },
  // Stall, then recover. Worth simulating because a real stall needs a genuinely
  // bad connection to reproduce, and because the interesting thing to eyeball is
  // that progress HOLDS at 60% rather than snapping back — a stalled batch is
  // still a transfer, so it keeps reporting uploadProgress.
  {
    state: 'stalled',
    durationMs: 2500,
    progressEnd: 0.6,
    timeLineText: 'Trying to reconnect…',
  },
  {
    state: 'uploading',
    durationMs: 2500,
    progressEnd: 1,
    timeLineText: 'About 2 sec left at 4 MB/s',
  },
  { state: 'wrapping-up', durationMs: 1500, progressEnd: 1, timeLineText: null },
  {
    state: 'processing',
    durationMs: 4000,
    progressEnd: 1,
    timeLineText: 'Feel free to check back later…',
  },
  { state: 'done-success', durationMs: 0, progressEnd: 1, timeLineText: null },
];

interface SimRecord {
  id: string;
  startedAt: number;
  stageIndex: number;
  /** Wall-clock when the current stage started — drives progress within it. */
  stageStartedAt: number;
  /** When true, the simulation's VM gets a fake `skippedDuplicates` list so
   * the BatchCard's "View N duplicates skipped" affordance renders. */
  withSkippedDuplicates?: boolean;
}

function buildSimVm(record: SimRecord, now: number): BatchCardViewModel {
  const stage = FULL_LIFECYCLE[record.stageIndex];
  const stageElapsed = now - record.stageStartedAt;
  const stageProgress = stage.durationMs === 0 ? 1 : Math.min(stageElapsed / stage.durationMs, 1);

  // Map stage progress into displayProgress. Each stage starts where the
  // previous one ended; we lerp inside.
  const prevEnd = record.stageIndex === 0 ? 0 : FULL_LIFECYCLE[record.stageIndex - 1].progressEnd;
  // Reset progress to 0 at the start of each phase that owns the bar
  // independently (uploading uses the 0–1 transfer bar, processing uses
  // the 0–1 server bar). Simplest: when state changes, the bar restarts
  // from 0 if the new stage's `progressEnd` is below the previous one's
  // — but in our timeline progress only ever increases through the
  // upload bar (queued/preparing/uploading) and then resets at processing.
  // Express that with explicit per-stage starts.
  const baseProgress = stage.state === 'processing' ? 0 : prevEnd;
  const displayProgress = baseProgress + (stage.progressEnd - baseProgress) * stageProgress;

  return {
    id: record.id,
    title: `Simulation ${record.id.slice(-4)}`,
    libraryName: 'Debug library',
    libraryUrl: '#',
    state: stage.state,
    mediaCount: 3,
    mediaTitles: ['vid1.mp4', 'vid2.mp4', 'vid3.mp4'],
    isAudio: false,
    thumbnailUrl: null,
    secondaryThumbnailUrl: null,
    gradientSeed: record.id,
    secondaryGradientSeed: `${record.id}-2`,
    primaryCdnPreview: null,
    secondaryCdnPreview: null,
    displayProgress,
    failedProgress: 0,
    timeLineText: stage.timeLineText,
    failedCount: 0,
    retryableCount: 0,
    succeededCount: 3,
    hasBillingFails: false,
    showCreditErrors: false,
    showStorageErrors: false,
    allConnectionFails: false,
    billedToAnotherAccount: false,
    timestamp: record.startedAt,
    skippedDuplicates: record.withSkippedDuplicates
      ? [
          {
            key: `${record.id}-dup-1`,
            filename: 'beach-trip.mp4',
            libraryId: 'demo-library-id',
            mediaId: 'demo-media-id-1',
          },
          {
            key: `${record.id}-dup-2`,
            filename: 'family-dinner.mp4',
            libraryId: 'demo-library-id',
            mediaId: 'demo-media-id-2',
          },
          {
            key: `${record.id}-dup-3`,
            filename: 'cat-video.mov',
            libraryId: 'demo-library-id',
            mediaId: 'demo-media-id-3',
          },
        ]
      : [],
  };
}

let simIdSeq = 0;

// ── Rich-event card demo seeds ─────────────────────────────────────────
// Each entry below mounts a fake `'rich-event'` notification with the
// shape the production code will push when the corresponding template
// fires. Lets us eyeball every iconKind / variant / CTA combination on
// the upload activity panel without going through a real upload flow.

const FAKE_REJECTED_ROWS: RichEventRow[] = [
  { key: 'r1', filename: 'notes.txt', reason: 'Unsupported file type' },
  { key: 'r2', filename: 'archive.zip', reason: 'Unsupported file type' },
  { key: 'r3', filename: 'installer.exe', reason: 'Unsupported file type' },
];

const FAKE_DUPLICATE_ROWS: RichEventRow[] = [
  {
    key: 'd1',
    filename: 'beach-trip.mp4',
    reason: 'Already in this library',
    isAudio: false,
    // Hardcoded demo IDs so the "View in library" button renders. The
    // actual route 404s — fine for a visual demo.
    viewInLibrary: {
      libraryId: 'demo-library-id',
      mediaId: 'demo-media-id-1',
      mediaName: 'beach-trip.mp4',
    },
  },
  {
    key: 'd2',
    filename: 'podcast-ep-12.mp3',
    reason: 'Already in this library',
    isAudio: true,
    viewInLibrary: {
      libraryId: 'demo-library-id',
      mediaId: 'demo-media-id-2',
      mediaName: 'podcast-ep-12.mp3',
    },
  },
];

const FAKE_PROCESSING_ROWS: RichEventRow[] = [
  {
    key: 'p1',
    filename: 'interview-part-1.mp4',
    reason: 'Internal processing error — please retry or contact support.',
  },
  {
    key: 'p2',
    filename: 'interview-part-2.mp4',
    reason: 'Internal processing error — please retry or contact support.',
  },
];

interface RichEventDemo {
  label: string;
  iconKind: RichEventIconKind;
  variant: 'error' | 'warning' | 'success';
  header: string;
  description?: string;
  rows: RichEventRow[];
  modalTitle: string;
  primaryActionLabel?: string;
  /** Optional library + status strip — when set the card uses the
   * BatchCard-style layout (filename + "+N others" title). */
  subtitle?: {
    libraryName: string;
    libraryUrl?: string;
    statusText: string;
    statusVariant?: 'error' | 'warning' | 'success' | 'info';
  };
  /** When set, the button fires through `pushRichEventOrMerge` with this
   * libraryId so repeated clicks demonstrate the 60s rolling-window merge
   * (used by the `media-processing-errored` template). */
  mergeLibraryId?: string;
}

const RICH_EVENT_DEMOS: RichEventDemo[] = [
  {
    label: 'drag-drop-all-rejected',
    iconKind: 'file-rejected',
    variant: 'error',
    header: 'All files rejected',
    description: 'All files you dragged were rejected.',
    rows: FAKE_REJECTED_ROWS,
    modalTitle: 'Rejected files',
  },
  {
    label: 'drag-drop-all-duplicates',
    iconKind: 'duplicate',
    variant: 'warning',
    header: '',
    rows: FAKE_DUPLICATE_ROWS,
    modalTitle: 'Duplicate files',
    subtitle: {
      libraryName: 'Default Library',
      libraryUrl: '/library/demo-library-id',
      statusText: 'Already in library',
      statusVariant: 'warning' as const,
    },
  },
  {
    label: 'media-processing-errored (merge demo)',
    iconKind: 'processing-error',
    variant: 'error',
    header: 'Processing failed',
    description: 'Click repeatedly within 60s to see rows merge into one card.',
    rows: FAKE_PROCESSING_ROWS,
    modalTitle: 'Processing errors',
    mergeLibraryId: 'debug-merge-library',
  },
];

let demoRowSeq = 0;
function freshenRows(rows: RichEventRow[]): RichEventRow[] {
  return rows.map((r) => ({ ...r, key: `${r.key}-${++demoRowSeq}` }));
}

function fireRichEventDemo(demo: RichEventDemo) {
  const rows = demo.mergeLibraryId ? freshenRows(demo.rows) : demo.rows;
  const primaryAction = demo.primaryActionLabel
    ? {
        label: demo.primaryActionLabel,
        // eslint-disable-next-line no-console
        onClick: () => console.log(`[debug] rich-event CTA clicked: ${demo.label}`),
      }
    : undefined;

  if (demo.mergeLibraryId) {
    notificationStore.pushRichEventOrMerge({
      type: 'rich-event',
      variant: demo.variant,
      iconKind: demo.iconKind,
      header: demo.header,
      description: demo.description,
      rows,
      modalTitle: demo.modalTitle,
      primaryAction,
      subtitle: demo.subtitle,
      libraryId: demo.mergeLibraryId,
      batchKind: 'media-processing-errored',
    });
  } else {
    notificationStore.add({
      type: 'rich-event',
      variant: demo.variant,
      iconKind: demo.iconKind,
      header: demo.header,
      description: demo.description,
      rows,
      modalTitle: demo.modalTitle,
      primaryAction,
      subtitle: demo.subtitle,
    });
  }
  // Same UX convention as `UploadManager.uploadFiles` — auto-open the
  // donut popover so the new card is immediately visible.
  notificationStore.setPopoverOpen(true);
}

// ── Component ──────────────────────────────────────────────────────────

const SurfaceBadges = ({ surfaces }: { surfaces: Surface[] }) => (
  <span className={styles.badges}>
    {surfaces.map((s) => (
      <span key={s} className={styles.badge} title={SURFACE_TITLE[s]}>
        {SURFACE_LABEL[s]}
      </span>
    ))}
  </span>
);

export const ToastDebugPage = observer(() => {
  const libraryManager = useLibraryManager();
  const realLibrary = libraryManager.defaultLibrary;
  const realLibraryId = realLibrary?.libraryId ?? null;
  const realLibraryName = realLibrary?.data?.name ?? 'Default Library';

  // ── In-flight simulations ──────────────────────────────────────────
  // Records own the lifecycle (stageIndex + stageStartedAt). The VM is
  // derived in render; we re-render via `now` ticked every 100ms while
  // any simulation is non-terminal.
  const [sims, setSims] = useState<SimRecord[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Advance any sim whose stage timer has elapsed; stop the tick once all
  // sims are terminal. Single shared interval for all running sims.
  const allTerminal =
    sims.length > 0 && sims.every((s) => FULL_LIFECYCLE[s.stageIndex].durationMs === 0);
  useEffect(() => {
    if (sims.length === 0 || allTerminal) {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return;
    }
    if (tickRef.current) return; // already running
    tickRef.current = setInterval(() => {
      const tickNow = Date.now();
      setSims((prev) => {
        let mutated = false;
        const next = prev.map((s) => {
          const stage = FULL_LIFECYCLE[s.stageIndex];
          if (stage.durationMs === 0) return s; // terminal, never advances
          const elapsed = tickNow - s.stageStartedAt;
          if (elapsed >= stage.durationMs && s.stageIndex < FULL_LIFECYCLE.length - 1) {
            mutated = true;
            return { ...s, stageIndex: s.stageIndex + 1, stageStartedAt: tickNow };
          }
          return s;
        });
        return mutated ? next : prev;
      });
      setNow(tickNow);
    }, 100);
  }, [sims.length, allTerminal]);

  useEffect(
    () => () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    },
    [],
  );

  const startSimulation = useCallback((opts?: { withSkippedDuplicates?: boolean }) => {
    simIdSeq++;
    const id = `sim-${simIdSeq.toString().padStart(4, '0')}`;
    const t = Date.now();
    setSims((prev) => [
      ...prev,
      {
        id,
        startedAt: t,
        stageIndex: 0,
        stageStartedAt: t,
        withSkippedDuplicates: opts?.withSkippedDuplicates,
      },
    ]);
  }, []);

  const clearSimulations = useCallback(() => setSims([]), []);

  if (!debugStore.respectfulDebugMode) {
    return <div style={{ width: '100vw', height: '100dvh' }} />;
  }

  const totalCount = SECTIONS.reduce((acc, s) => acc + s.toasts.length, 0);

  return (
    <div className={styles.page}>
      {/* Top bar — mirrors what would normally live in <PageHeader> on
          regular routes. The upload donut is rendered here so fake batches
          and in-flight simulations added below are visible inside its
          popover. The bell placeholder shows where Phase 6's notification
          bell will sit. */}
      <div className={styles.topBar}>
        <span className={styles.topBarPlaceholder} title="Bell — coming in Phase 6">
          🔔
        </span>
        {debugStore.showUploadStatus ? (
          <UploadStatus
            simulationCards={
              sims.length > 0
                ? sims.map((s) => (
                    <BatchCard
                      key={s.id}
                      vm={buildSimVm(s, now)}
                      onDismiss={() => setSims((prev) => prev.filter((x) => x.id !== s.id))}
                      formatTime={(ts) => `started ${Math.round((now - ts) / 1000)}s ago`}
                    />
                  ))
                : undefined
            }
          />
        ) : (
          <span className={styles.topBarHint}>
            Enable <code>debug.uploadStatus</code> in Settings → Debug to see the upload donut →
          </span>
        )}
      </div>

      <header className={styles.header}>
        <h1 className={styles.title}>Toast catalog</h1>
        <p className={styles.subtitle}>
          Every template in <code>src/lib/utils/toastmaster.tsx</code>. Click a button to fire that
          template with sample data. {totalCount} templates total.
        </p>
        <p className={styles.legend}>
          <span className={styles.badge}>{SURFACE_LABEL.toast}</span> = renders as toast{' '}
          &nbsp;·&nbsp;
          <span className={styles.badge}>{SURFACE_LABEL.bell}</span> = adds bell entry &nbsp;·&nbsp;
          <span className={styles.badge}>{SURFACE_LABEL.panel}</span> = panel-only (batch-card row)
          &nbsp;·&nbsp; surfaces reflect the <em>planned</em> routing per Phase 5.
        </p>
      </header>

      {/* ── Fake upload batches ─────────────────────────────────────── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          Upload status panel — fake batches{' '}
          <span className={styles.sectionCount}>(open the donut popover to see them)</span>
        </h2>
        <p className={styles.hint}>
          Adds notifications in terminal states so polling skips them. In-flight states (queued /
          uploading / processing) require a real upload — kick off any actual file upload to
          exercise those. Buttons disable until a real library has loaded (we tag fake batches with
          one of your libraries so backend auth doesn't reject them).
        </p>
        <div className={styles.row}>
          <button
            className={styles.toastButton}
            disabled={!realLibraryId}
            onClick={() => realLibraryId && addFakeSuccessBatch(realLibraryId)}
          >
            Add success batch (3 files)
          </button>
          <button
            className={styles.toastButton}
            disabled={!realLibraryId}
            onClick={() => realLibraryId && addFakePartialFailureBatch(realLibraryId)}
          >
            Add partial failure (3 of 5)
          </button>
          <button
            className={styles.toastButton}
            disabled={!realLibraryId}
            onClick={() => realLibraryId && addFakeTotalFailureBatch(realLibraryId)}
          >
            Add total failure (3 files)
          </button>
          <button
            className={styles.toastButton}
            disabled={!realLibraryId}
            onClick={() => realLibraryId && addFakeUploadFailureBatch(realLibraryId)}
          >
            Add upload failure (connection)
          </button>
          <button
            className={styles.toastButton}
            disabled={!realLibraryId}
            onClick={() => realLibraryId && addFakeUrlImportBatch(realLibraryId)}
          >
            Add successful URL import
          </button>
          <button
            className={`${styles.toastButton} ${styles.dangerButton}`}
            onClick={clearAllBatches}
          >
            Clear all batches
          </button>
        </div>
        {realLibraryId && (
          <p className={styles.hint}>
            Using library <code>{realLibraryName}</code> ({realLibraryId.slice(0, 8)}…) for the fake
            batches.
          </p>
        )}
      </section>

      {/* ── In-flight BatchCard simulations ────────────────────────── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          BatchCard in-flight simulation{' '}
          <span className={styles.sectionCount}>(direct mount, no upload required)</span>
        </h2>
        <p className={styles.hint}>
          Mounts the dumb <code>&lt;BatchCard&gt;</code> directly with hand-built VMs and runs each
          one through the lifecycle (queued → preparing → uploading → wrapping-up → processing →
          done-success). No real upload, no notificationStore, no uploadManager. Cards appear in the
          donut popover at the top of this page, under a "Simulations (debug)" header.
        </p>
        <div className={styles.row}>
          <button className={styles.toastButton} onClick={() => startSimulation()}>
            Start full lifecycle simulation
          </button>
          <button
            className={styles.toastButton}
            onClick={() => startSimulation({ withSkippedDuplicates: true })}
          >
            Start sim with 3 skipped duplicates
          </button>
          <button
            className={`${styles.toastButton} ${styles.dangerButton}`}
            onClick={clearSimulations}
            disabled={sims.length === 0}
          >
            Clear simulations
          </button>
        </div>
        {sims.length > 0 && (
          <p className={styles.hint}>
            {sims.length} active. Click the donut at the top of the page to see them.
          </p>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          Rich event cards <span className={styles.sectionCount}>({RICH_EVENT_DEMOS.length})</span>
        </h2>
        <p className={styles.hint}>
          New "big" card variant — click to push directly into the upload activity panel (bypasses
          toast). Open the donut at the top to see them. The processing-error demo uses the
          rolling-window merge: click it more than once within 60s to watch additional rows fold
          into the same card.
        </p>
        <div className={styles.row}>
          {RICH_EVENT_DEMOS.map((demo) => (
            <button
              key={demo.label}
              className={styles.toastButton}
              onClick={() => fireRichEventDemo(demo)}
              title={`${demo.iconKind} · ${demo.variant}`}
            >
              <SurfaceBadges surfaces={['panel']} />
              <span className={styles.buttonId}>{demo.label}</span>
              <span className={styles.buttonNote}>
                {demo.iconKind} · {demo.variant}
              </span>
            </button>
          ))}
        </div>
      </section>

      {SECTIONS.map((section) => (
        <section key={section.title} className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {section.title} <span className={styles.sectionCount}>({section.toasts.length})</span>
          </h2>
          {section.hint && <p className={styles.hint}>{section.hint}</p>}
          <div className={styles.row}>
            {section.toasts.map(({ id, data, surfaces = ['toast'], note }) => {
              // See `ToastDef.data`: resolved here, never where `SECTIONS` is written.
              const payload = typeof data === 'function' ? data() : data;
              return (
                <button
                  key={`${id}-${note ?? ''}`}
                  className={styles.toastButton}
                  onClick={() => showToast(id, payload)}
                  title={buttonTooltip(id, payload)}
                >
                  <SurfaceBadges surfaces={surfaces} />
                  <span className={styles.buttonId}>{id}</span>
                  {note && <span className={styles.buttonNote}>{note}</span>}
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
});
